<script setup>
import { ref, computed } from 'vue';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  modelValue: {
    type: String,
    default: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
  },
  isDarkMode: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const isMenuOpen = ref(false);

// Sort options using existing constants and i18n keys
const sortOptions = computed(() => [
  {
    value: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_DESC,
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.last_activity_at_desc.TEXT'),
    icon: 'i-lucide-clock',
  },
  {
    value: wootConstants.SORT_BY_TYPE.LAST_ACTIVITY_AT_ASC,
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.last_activity_at_asc.TEXT'),
    icon: 'i-lucide-clock',
  },
  {
    value: wootConstants.SORT_BY_TYPE.CREATED_AT_DESC,
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.created_at_desc.TEXT'),
    icon: 'i-lucide-calendar-plus',
  },
  {
    value: wootConstants.SORT_BY_TYPE.CREATED_AT_ASC,
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.created_at_asc.TEXT'),
    icon: 'i-lucide-calendar-plus',
  },
  {
    value: wootConstants.SORT_BY_TYPE.PRIORITY_DESC,
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_desc.TEXT'),
    icon: 'i-lucide-signal-high',
  },
  {
    value: wootConstants.SORT_BY_TYPE.PRIORITY_ASC,
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.priority_asc.TEXT'),
    icon: 'i-lucide-signal-low',
  },
  {
    value: wootConstants.SORT_BY_TYPE.WAITING_SINCE_ASC,
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.waiting_since_asc.TEXT'),
    icon: 'i-lucide-hourglass',
  },
  {
    value: wootConstants.SORT_BY_TYPE.WAITING_SINCE_DESC,
    label: t('CHAT_LIST.SORT_ORDER_ITEMS.waiting_since_desc.TEXT'),
    icon: 'i-lucide-hourglass',
  },
]);

const currentSortLabel = computed(() => {
  const option = sortOptions.value.find(opt => opt.value === props.modelValue);
  return option?.label || t('CHAT_LIST.SORT_TOOLTIP_LABEL');
});

const toggleMenu = () => {
  isMenuOpen.value = !isMenuOpen.value;
};

const closeMenu = () => {
  isMenuOpen.value = false;
};

const handleSelect = value => {
  emit('update:modelValue', value);
  isMenuOpen.value = false;
};

// Dynamic classes based on dark mode
const buttonBgClass = computed(() => {
  if (isMenuOpen.value) {
    return props.isDarkMode ? 'bg-kpt-dark-600' : 'bg-kpt-light-300';
  }
  return props.isDarkMode
    ? 'bg-kpt-dark-700 hover:bg-kpt-dark-600'
    : 'bg-kpt-light-200 hover:bg-kpt-light-300';
});

const iconClass = computed(() => {
  return props.isDarkMode
    ? 'text-kpt-text-secondary-dark'
    : 'text-kpt-text-secondary-light';
});

const menuBgClass = computed(() => {
  return props.isDarkMode
    ? 'bg-kpt-dark-800 border-kpt-border-dark'
    : 'bg-kpt-light-50 border-kpt-border-light';
});

const menuItemHoverClass = computed(() => {
  return props.isDarkMode ? 'hover:bg-kpt-dark-700' : 'hover:bg-kpt-light-200';
});

const menuItemTextClass = computed(() => {
  return props.isDarkMode
    ? 'text-kpt-text-primary-dark'
    : 'text-kpt-text-primary-light';
});

const menuItemIconClass = computed(() => {
  return props.isDarkMode
    ? 'text-kpt-text-secondary-dark'
    : 'text-kpt-text-secondary-light';
});

const selectedBgClass = computed(() => {
  return props.isDarkMode ? 'bg-kpt-dark-600' : 'bg-kpt-light-300';
});
</script>

<template>
  <div v-on-clickaway="closeMenu" class="relative">
    <!-- NOTICE: KAPTION-INTEGRATION - rounded-full for circular appearance -->
    <button
      v-tooltip.bottom="currentSortLabel"
      class="flex items-center justify-center w-8 h-8 rounded-full transition-all duration-200 hover:scale-110 active:scale-95"
      :class="buttonBgClass"
      @click="toggleMenu"
    >
      <span class="i-lucide-arrow-up-down h-4 w-4" :class="iconClass" />
    </button>

    <!-- Dropdown Menu - NOTICE: KAPTION-INTEGRATION - rounded-3xl for rounder appearance -->
    <div
      v-if="isMenuOpen"
      class="absolute top-full mt-2 ltr:left-0 rtl:right-0 z-50 min-w-64 rounded-3xl border shadow-lg overflow-hidden"
      :class="menuBgClass"
    >
      <!-- Header -->
      <div
        class="px-3 py-2 border-b"
        :class="
          isDarkMode
            ? 'border-kpt-border-dark text-kpt-text-secondary-dark'
            : 'border-kpt-border-light text-kpt-text-secondary-light'
        "
      >
        <span class="text-xs font-medium uppercase tracking-wider">
          {{ t('CHAT_LIST.CHAT_SORT.ORDER_BY') }}
        </span>
      </div>

      <!-- Sort Options -->
      <div class="py-1">
        <button
          v-for="option in sortOptions"
          :key="option.value"
          class="flex items-center gap-3 w-full px-3 py-2 text-left transition-colors duration-150"
          :class="[
            menuItemHoverClass,
            modelValue === option.value ? selectedBgClass : '',
          ]"
          @click="handleSelect(option.value)"
        >
          <span
            class="h-4 w-4 flex-shrink-0"
            :class="[option.icon, menuItemIconClass]"
          />
          <span class="flex-1 text-sm" :class="menuItemTextClass">
            {{ option.label }}
          </span>
          <span
            v-if="modelValue === option.value"
            class="i-lucide-check h-4 w-4 flex-shrink-0 text-kpt-emerald"
          />
        </button>
      </div>
    </div>
  </div>
</template>
